import { useState, useEffect, useRef } from 'react';
import { getTranscription } from '../utils/api';
import { TranscriptionSegment } from '../types';

interface UseTranscriptionReturn {
  transcriptionSegments: TranscriptionSegment[] | null;
  isTranscribing: boolean;
}

export const useTranscription = (evaluationId: string | null): UseTranscriptionReturn => {
  const [transcriptionSegments, setTranscriptionSegments] = useState<TranscriptionSegment[] | null>(null);
  const [isTranscribing, setIsTranscribing] = useState<boolean>(false);
  // Trace l'évaluation à laquelle les segments en état appartiennent
  const segmentsOwnerRef = useRef<string | null>(null);

  useEffect(() => {
    // Nouvelle évaluation : purger la transcription précédente
    if (segmentsOwnerRef.current !== evaluationId) {
      segmentsOwnerRef.current = evaluationId;
      setTranscriptionSegments(null);
      setIsTranscribing(false);
    }

    if (!evaluationId || transcriptionSegments) {
      return;
    }

    let cancelled = false;
    setIsTranscribing(true);

    const poll = async () => {
      try {
        const result = await getTranscription(evaluationId);

        if (result.status === 'completed' || result.status === 'finished') {
          if (result.segments && !cancelled) {
            // Ne publier que si on est toujours sur la même évaluation
            if (segmentsOwnerRef.current === evaluationId) {
              setTranscriptionSegments(result.segments);
            }
            setIsTranscribing(false);
          }
          return true;
        }
        if (result.status === 'failed') {
          if (!cancelled) setIsTranscribing(false);
          return true;
        }
      } catch (err) {
        if (!cancelled) setIsTranscribing(false);
        return true;
      }
      return false;
    };

    const intervalId = setInterval(async () => {
      const done = await poll();
      if (done && !cancelled) {
        clearInterval(intervalId);
      }
    }, 3000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      setIsTranscribing(false);
    };
  }, [evaluationId, transcriptionSegments]);

  return { transcriptionSegments, isTranscribing };
};
